import type { UIMessage } from "ai";
import { useEffect, useRef, useState } from "react";

import { logger } from "~/libs/logger";
import { trpc } from "~/libs/trpc.lib";
import { ChatMessages } from "./chat-messages";
import { useThreadSubscription } from "./use-thread-subscription";

const noop = () => {};

/** Collects the set of tool-call ids referenced by a message's parts. */
const collectToolCallIds = (message: UIMessage): Set<string> => {
  const ids = new Set<string>();
  for (const part of message.parts ?? []) {
    const type = (part as { type?: unknown }).type;
    if (typeof type !== "string" || !type.startsWith("tool-")) continue;
    const id = (part as { toolCallId?: unknown }).toolCallId;
    if (typeof id === "string") ids.add(id);
  }
  return ids;
};

/**
 * Merges the recalled `history` with the live in-progress `liveMessage`,
 * dropping any history message that shares a tool-call id with the live turn.
 *
 * Mastra persists the in-progress run into thread memory as it streams, so the
 * recalled `history` can already contain the same assistant turn that `observe`
 * is replaying into `liveMessage`. Without this filter the tool calls (and the
 * assistant text around them) would render twice. Tool-call ids are globally
 * unique, so a shared id unambiguously marks the duplicate.
 */
const mergeLiveTurn = (
  history: UIMessage[],
  liveMessage: UIMessage | null,
): UIMessage[] => {
  if (!liveMessage) return history;
  const liveToolCallIds = collectToolCallIds(liveMessage);
  if (liveToolCallIds.size === 0) return [...history, liveMessage];
  const deduped = history.filter((m) => {
    for (const id of collectToolCallIds(m)) {
      if (liveToolCallIds.has(id)) return false;
    }
    return true;
  });
  return [...deduped, liveMessage];
};

export type GeneratingThreadViewProps = {
  threadId: string;
  /**
   * The durable run id to tail via `observe`, read from the thread metadata.
   * When `null` (run already finished + cache expired), the view falls through
   * to the recalled transcript immediately.
   */
  runId: string | null;
  /**
   * Called once the background run finishes (or there is no live run to tail),
   * so the parent can hand back to the interactive `useChat` transcript.
   * `hadError` is true when the live tail ended on an error (e.g. the observe
   * connection dropped), so the parent can explain why the live view vanished.
   */
  onDone: (threadId: string, hadError: boolean) => void;
  /**
   * Forwarded to {@link ChatMessages} so the live view matches the full-page
   * layout's centered reading column while the scroll container spans the pane.
   */
  contentClassName?: string;
};

/**
 * Read-only view shown while a thread's background supervisor run is still
 * generating. Renders the persisted history plus the live in-progress
 * assistant turn (reasoning + tool calls), tailed from the current point via
 * {@link useThreadSubscription}. The chat input is disabled by the parent
 * while this is mounted; on completion we call `onDone` and the parent swaps
 * back to the normal editable transcript.
 */
export const GeneratingThreadView = ({
  threadId,
  runId,
  onDone,
  contentClassName,
}: GeneratingThreadViewProps) => {
  const [history, setHistory] = useState<UIMessage[]>([]);
  const { message: liveMessage, status } = useThreadSubscription(runId, true);

  // Hydrate the persisted history once so the seed user message (and anything
  // already saved) shows beneath the live turn.
  useEffect(() => {
    let cancelled = false;
    void trpc.ai.messages
      .query({ threadId })
      .then((msgs) => {
        if (!cancelled) setHistory(msgs as UIMessage[]);
      })
      .catch((err) =>
        logger.error({ err }, "Failed to hydrate generating thread history"),
      );
    return () => {
      cancelled = true;
    };
  }, [threadId]);

  // Fallback poll for degraded live delivery. Mastra persists the in-progress
  // run into thread memory as it streams, so re-fetching the history surfaces
  // progress even when the `observe` tail is silent — e.g. a multi-instance
  // deployment where the in-process cache/pubsub can't see a run that started
  // on another replica. We only poll while `idle` (connected with a run to
  // tail but no chunks yet); once chunks stream or the run settles, the live
  // turn (or the parent's handback via `onDone`) takes over and we stop.
  useEffect(() => {
    if (status !== "idle" || runId === null) return;
    let cancelled = false;
    const interval = setInterval(() => {
      void trpc.ai.messages
        .query({ threadId })
        .then((msgs) => {
          if (!cancelled) setHistory(msgs as UIMessage[]);
        })
        .catch((err) =>
          logger.error({ err }, "Failed to poll generating thread history"),
        );
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [threadId, runId, status]);

  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    if (status === "done" || status === "error") {
      onDoneRef.current(threadId, status === "error");
    }
  }, [status, threadId]);

  const messages = mergeLiveTurn(history, liveMessage);
  // Show the "Thinking..." loader both while streaming and during the initial
  // connect/replay window (idle with a run to tail), so the view doesn't look
  // frozen before the first chunk arrives while the input stays disabled.
  const isBusy =
    status === "streaming" || (status === "idle" && runId !== null);

  return (
    <ChatMessages
      messages={messages}
      isBusy={isBusy}
      onToolApproval={noop}
      onToolResume={noop}
      contentClassName={contentClassName}
    />
  );
};
